/**
 * GIFT GUARD — the per-business settings for prize cards activated by text:
 * how many cards one phone and one player may activate per night, the one
 * number the texts go to (super admin), and the venue's reservation link with
 * its own one-tap switch.
 */
import { useCallback, useEffect, useState } from 'react';
import { CalendarCheck, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';

export const GoalGiftGuardBar = ({ organizationId, isSuperAdmin = false }) => {
    const [cfg, setCfg] = useState(null);
    // What is actually stored, so a blur with no change saves nothing.
    const [saved, setSaved] = useState({});
    const [number, setNumber] = useState('');
    const [pretty, setPretty] = useState('');
    const [booking, setBooking] = useState(null);
    const [link, setLink] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const [g, b] = await Promise.all([
                api.get('/goal-game/admin/guard-settings', { params: { organization_id: organizationId } }),
                api.get('/goal-game/admin/booking-link', { params: { organization_id: organizationId } }),
            ]);
            setCfg(g.data.settings);
            setSaved(g.data.settings);
            setNumber(g.data.number || '');
            setPretty(g.data.pretty || '');
            setBooking(b.data);
            setLink(b.data.url || '');
        } catch (_e) { setCfg(null); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const saveCaps = async (patch) => {
        setBusy(true);
        try {
            const res = await api.put('/goal-game/admin/guard-settings', {
                organization_id: organizationId, ...patch });
            setCfg(res.data.settings);
            setSaved(res.data.settings);
            toast.success('Saved');
        } catch (_e) { toast.error('Could not save'); } finally { setBusy(false); }
    };

    const saveNumber = async (value) => {
        setBusy(true);
        try {
            const next = value === undefined ? number : value;
            const res = await api.put('/goal-game/admin/activation-number', { number: next });
            setNumber(res.data.number || '');
            setPretty(res.data.pretty || '');
            toast.success(res.data.number ? 'Saved' : 'Number cleared');
        } catch (_e) { toast.error('Could not save'); } finally { setBusy(false); }
    };

    const saveBooking = async (patch) => {
        setBusy(true);
        try {
            const res = await api.put('/goal-game/admin/booking-link', {
                organization_id: organizationId, ...patch });
            setBooking(res.data);
            setLink(res.data.url || '');
            toast.success('Saved');
        } catch (_e) { toast.error('Could not save'); } finally { setBusy(false); }
    };

    if (!cfg) return null;

    const num = (key, label) => (
        <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            {label}
            <input type="number" min={0} max={50} value={cfg[key] ?? 0}
                   onChange={(e) => setCfg({ ...cfg, [key]: Number(e.target.value) })}
                   onBlur={() => {
                       const next = Number(cfg[key] ?? 0);
                       if (next !== Number(saved[key] ?? 0)) saveCaps({ [key]: next });
                   }}
                   data-testid={`gg-guard-${key}`}
                   className="w-16 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-2 py-1 tabular-nums" />
        </label>
    );

    return (
        <div data-testid={`gg-guard-bar-${organizationId}`}
             className="space-y-3 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
            <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold inline-flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5" />Activate by text
                </span>
                {num('max_per_phone_per_night', 'Cards per phone, per night')}
                {num('max_per_player_per_night', 'Cards per player, per night')}
                {busy ? <Loader2 size={13} className="animate-spin text-slate-400" /> : null}
            </div>
            {isSuperAdmin && (
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-500">Our number for activation texts</span>
                    <input type="tel" value={number} onChange={(e) => setNumber(e.target.value)}
                           placeholder="+1 514 555 0123"
                           data-testid="gg-guard-number"
                           className="min-w-[180px] rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-1.5 text-sm" />
                    {pretty && (
                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300"
                              data-testid="gg-guard-number-pretty">{pretty}</span>
                    )}
                    <button type="button" onClick={() => saveNumber()} disabled={busy}
                            data-testid="gg-guard-number-save"
                            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white dark:bg-white dark:text-slate-900">
                        Save
                    </button>
                    <button type="button" onClick={() => { setNumber(''); saveNumber(''); }} disabled={busy}
                            data-testid="gg-guard-number-clear"
                            className="rounded-lg border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm font-semibold text-muted-foreground">
                        Clear
                    </button>
                    {!number && (
                        <span className="text-xs text-amber-600" data-testid="gg-guard-number-missing">
                            No number yet — players cannot activate until this is set.
                        </span>
                    )}
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 dark:border-slate-800 pt-3">
                <span className="text-sm font-semibold inline-flex items-center gap-1.5">
                    <CalendarCheck className="w-3.5 h-3.5" />Show “Make a reservation” button
                </span>
                <button type="button" onClick={() => saveBooking({ enabled: !booking?.enabled })}
                        disabled={busy} aria-pressed={!!booking?.enabled}
                        data-testid="gg-booking-switch"
                        className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${
                            booking?.enabled ? 'bg-emerald-600 text-white border-emerald-600'
                                : 'bg-white dark:bg-slate-900 text-muted-foreground border-slate-300 dark:border-slate-700'}`}>
                    {booking?.enabled ? 'On' : 'Off'}
                </button>
                <input type="url" value={link} onChange={(e) => setLink(e.target.value)}
                       placeholder="https://your-booking-page"
                       data-testid="gg-booking-link"
                       className="min-w-[220px] flex-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm" />
                <button type="button" onClick={() => saveBooking({ url: link })} disabled={busy}
                        data-testid="gg-booking-save"
                        className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white dark:bg-white dark:text-slate-900">
                    Save
                </button>
                {booking?.needs_link && (
                    <span className="text-xs text-amber-600" data-testid="gg-booking-needs-link">
                        Add your booking link first
                    </span>
                )}
            </div>
        </div>
    );
};

export default GoalGiftGuardBar;
